import React, { useEffect, useState } from "react";
import api from "../setupAxios";
import { useDialog } from "../context/dialogContext";
import { isoToEs } from "../fechas";
import Badge from "./Badge";
import ComponenteCampos, { ICONO_COMPONENTE } from "./ComponenteCampos";

// Subconjunto de editComp que cuenta como "cambio real" para el aviso de
// cambios sin guardar (deja fuera guardando/msg, que cambian solos y no son
// ediciones del usuario).
const snapshotEditComp = (ec) => JSON.stringify({
  portal: ec.portal, piso: ec.piso, buzon: ec.buzon,
  segProveedor: ec.segProveedor, aplicarPiso: ec.aplicarPiso,
});

// Ficha de UNA llave con sus componentes (portal/piso/buzón), editable —
// el mismo modal "Info" de Inventario llaves, reutilizable desde cualquier
// sitio que solo tenga a mano el `llaveId` (Inventario, Historial...): se
// autoabastece con GET /llave/<id>/info en vez de depender de que el listado
// que lo abrió ya trajera todos estos datos precargados.
export default function InfoLlaveModal({ llaveId, onClose, onGuardado }) {
  const [info, setInfo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [editComp, setEditComp] = useState(null);
  // Comentario de la llave: null = no se está editando; si no, el texto en edición.
  const [comEdit, setComEdit] = useState(null);
  const [comGuardando, setComGuardando] = useState(false);
  const infoInicialRef = React.useRef(null);
  const { avisar, confirmar } = useDialog();

  useEffect(() => {
    api.get(`/llave/${llaveId}/info`).then(({ data }) => {
      if (!data.ok) { setCargando(false); return; }
      const porTipo = (t) => {
        const c = (data.componentes || []).find((x) => x.tipo === t);
        return { tiene: !!c, mec: c?.mecanismo || "fisica", tarj: !!c?.tarjeta, seg: !!c?.es_seguridad };
      };
      const conSeg = (data.componentes || []).find((x) => x.es_seguridad);
      const inicial = {
        portal: porTipo("portal"), piso: porTipo("piso"), buzon: porTipo("buzon"),
        segProveedor: conSeg?.seg_proveedor || "",
        aplicarPiso: false, guardarDefault: false, guardando: false, msg: null,
      };
      setInfo(data);
      setEditComp(inicial);
      infoInicialRef.current = snapshotEditComp(inicial);
      setCargando(false);
    }).catch(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llaveId]);

  const cerrar = async () => {
    if (editComp?.guardando) return; // no cerrar a media escritura
    const comCambiado = comEdit !== null && comEdit.trim() !== (info?.comentario || "");
    if ((editComp && snapshotEditComp(editComp) !== infoInicialRef.current) || comCambiado) {
      const ok = await confirmar("Hay cambios sin guardar en esta llave. ¿Cerrar sin guardarlos?");
      if (!ok) return;
    }
    onClose();
  };
  const set1 = (k, campo, v) => setEditComp((prev) => ({ ...prev, [k]: { ...prev[k], [campo]: v } }));

  const guardarComentario = async () => {
    setComGuardando(true);
    try {
      const { data } = await api.post(`/llave/${llaveId}/comentario`, { comentario: comEdit });
      if (data.ok) {
        setInfo((prev) => ({ ...prev, comentario: data.comentario }));
        setComEdit(null);
        onGuardado?.();
      } else avisar(data.error || "No se pudo guardar el comentario.");
    } catch (err) {
      avisar(err.response?.data?.error || "No se pudo guardar el comentario.");
    } finally { setComGuardando(false); }
  };
  const haySeg = editComp && (editComp.portal.seg || editComp.piso.seg || editComp.buzon.seg);

  const guardarComponentes = async () => {
    if (editComp.aplicarPiso) {
      const ok = await confirmar(`¿Aplicar esta configuración a TODAS las llaves activas del piso ${info.piso}? Se sobrescribirá su mecanismo/tarjeta/seguridad (no su fecha de copia).`);
      if (!ok) return;
    }
    setEditComp((prev) => ({ ...prev, msg: null, guardando: true }));
    try {
      const { data } = await api.post(`/llave/${llaveId}/detalle`, {
        tiene_portal: editComp.portal.tiene, mec_portal: editComp.portal.mec, tarj_portal: editComp.portal.tarj, seg_portal: editComp.portal.seg,
        tiene_piso: editComp.piso.tiene, mec_piso: editComp.piso.mec, tarj_piso: editComp.piso.tarj, seg_piso: editComp.piso.seg,
        tiene_buzon: editComp.buzon.tiene, mec_buzon: editComp.buzon.mec, tarj_buzon: editComp.buzon.tarj, seg_buzon: editComp.buzon.seg,
        seg_proveedor: editComp.segProveedor.trim() || null,
        aplicar_a_piso: editComp.aplicarPiso, guardar_default: editComp.guardarDefault,
      });
      if (data.ok) {
        onGuardado?.();
        onClose();
        avisar(editComp.aplicarPiso ? `Actualizadas ${data.actualizadas} llave(s) de ese piso.` : "Componentes guardados.");
      } else {
        setEditComp((prev) => ({ ...prev, guardando: false, msg: data.error || "No se pudo guardar." }));
      }
    } catch (err) {
      setEditComp((prev) => ({ ...prev, guardando: false, msg: err.response?.data?.error || "Error de conexión." }));
    }
  };

  // Función (no componente <FilaComponente/>): así React no recrea la fila en
  // cada tecla y los campos no pierden el foco.
  const filaComponente = (k, nombre, fechaCopia) => (
    <div key={k} className={`componente-row t-${k}${editComp[k].tiene ? " activo" : ""}`}>
      <label className="componente-row-head">
        <input type="checkbox" checked={editComp[k].tiene} onChange={(e) => set1(k, "tiene", e.target.checked)} />
        <span className="cr-ico">{ICONO_COMPONENTE[k]}</span>
        <span className="cr-nombre">{nombre}</span>
        {editComp[k].tiene && fechaCopia && <span className="fecha-copia">Copia: {isoToEs(fechaCopia)}</span>}
      </label>
      {editComp[k].tiene && (
        <ComponenteCampos tipo={k} valor={editComp[k]} onChange={(campo, v) => set1(k, campo, v)} />
      )}
    </div>
  );

  if (cargando) {
    return (
      <div className="modal-back" onMouseDown={onClose}>
        <div className="modal modal-lg" onMouseDown={(e) => e.stopPropagation()}>
          <p className="muted">Cargando…</p>
        </div>
      </div>
    );
  }
  if (!info || !editComp) {
    return (
      <div className="modal-back" onMouseDown={onClose}>
        <div className="modal modal-sm" onMouseDown={(e) => e.stopPropagation()}>
          <p className="alert alert-error">No se pudo cargar esta llave.</p>
          <div className="modal-actions"><button className="btn" onClick={onClose}>Cerrar</button></div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-back" onMouseDown={cerrar}>
      <div className="modal modal-lg" onMouseDown={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <h3 style={{ margin: 0 }}>🔑 {info.piso} · llave {info.codigo ?? "—"}</h3>
          {info.estado === "perdidas" ? <Badge tone="red">Perdida</Badge>
            : info.situacion === "prestada" ? <Badge tone="amber">Prestada</Badge>
            : <Badge tone="green">En cajetín</Badge>}
        </div>

        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Cajetín</span>
            <span className="info-value">{info.cajetin ?? "—"}</span>
          </div>
          {info.creada_por && (
            <div className="info-item">
              <span className="info-label">Creada por</span>
              <span className="info-value">{info.creada_por}</span>
            </div>
          )}
          {info.situacion === "prestada" && (
            <>
              <div className="info-item">
                <span className="info-label">Quién</span>
                <span className="info-value">{info.quien || "—"}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Tipo</span>
                <span className="info-value">{info.tipo_prestamo === "fija" ? "Fija" : "Provisional"}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Salida</span>
                <span className="info-value">{isoToEs(info.salida) || "—"}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Devolución estimada</span>
                <span className="info-value">{isoToEs(info.devol) || "—"}</span>
              </div>
            </>
          )}
        </div>
        <div className="coment-llave">
          <div className="cl-head">
            💬 Comentario de la llave
            <span style={{ flex: 1 }} />
            {comEdit === null && (
              <button className="btn btn-mini" onClick={() => setComEdit(info.comentario || "")}>
                ✏️ {info.comentario ? "Editar" : "Añadir"}
              </button>
            )}
          </div>
          {comEdit === null ? (
            <div className="cl-texto">{info.comentario || <span className="muted">Sin comentario.</span>}</div>
          ) : (
            <>
              <textarea rows={3} value={comEdit} maxLength={2000} autoFocus
                        placeholder="Ej: copia de repuesto, tarjeta con la esquina rota…"
                        onChange={(e) => setComEdit(e.target.value)} />
              <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 6 }}>
                <button className="btn btn-mini" onClick={() => setComEdit(null)} disabled={comGuardando}>Cancelar</button>
                <button className="btn btn-mini btn-primary" onClick={guardarComentario} disabled={comGuardando}>
                  {comGuardando ? "Guardando…" : "Guardar comentario"}
                </button>
              </div>
            </>
          )}
        </div>

        {info.ruta_id ? (
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">De la ruta</span>
              <span className="info-value">#{info.ruta_id}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Fecha pedida</span>
              <span className="info-value">{isoToEs(info.ruta_pedida) || "—"}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Fecha preparada</span>
              <span className="info-value">{isoToEs(info.ruta_preparada) || "—"}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Fecha entrega</span>
              <span className="info-value">{isoToEs(info.ruta_entrega) || "—"}</span>
            </div>
          </div>
        ) : (
          info.situacion === "prestada" && <p className="muted">Prestada directamente desde Gestionar (no viene de una ruta).</p>
        )}

        <hr className="modal-hr" />
        <div className="section-title" style={{ margin: "0 0 4px", fontSize: 15 }}>🧩 Componentes de la llave</div>
        <p className="muted" style={{ margin: "0 0 4px" }}>Qué mecanismo abre cada elemento (portal, piso, buzón) y si tiene copia de seguridad.</p>
        {editComp.msg && <div className="alert alert-error">{editComp.msg}</div>}
        {filaComponente("portal", "Portal", info.componentes?.find((c) => c.tipo === "portal")?.fecha_copia)}
        {filaComponente("piso", "Piso", info.componentes?.find((c) => c.tipo === "piso")?.fecha_copia)}
        {filaComponente("buzon", "Buzón", info.componentes?.find((c) => c.tipo === "buzon")?.fecha_copia)}
        {haySeg && (
          <div className="seg-box">
            <label>🔒 Proveedor de seguridad</label>
            <input value={editComp.segProveedor} onChange={(e) => setEditComp((p) => ({ ...p, segProveedor: e.target.value }))} />
          </div>
        )}
        <div className="warn-box">
          <label>
            <input type="checkbox" checked={editComp.aplicarPiso}
                   onChange={(e) => setEditComp((p) => ({ ...p, aplicarPiso: e.target.checked }))} />
            <span>⚠️ Aplicar esta misma configuración a <b>todas las llaves activas</b> de este piso (no sólo a esta)</span>
          </label>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10 }}>
          <input type="checkbox" checked={editComp.guardarDefault}
                 onChange={(e) => setEditComp((p) => ({ ...p, guardarDefault: e.target.checked }))} />
          Usar esta configuración como la habitual de este piso (precargará las próximas llaves nuevas)
        </label>

        <div className="modal-actions">
          <button className="btn" onClick={cerrar} disabled={editComp.guardando}>Cancelar</button>
          <button className="btn btn-primary" onClick={guardarComponentes} disabled={editComp.guardando}>
            {editComp.guardando ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}
